import { useState, useCallback, useEffect, useRef } from "react"
import {
  fetchBookings, fetchBooking, createBooking, updateBookingCustomer,
  cancelBooking, completeBooking, recordPayment, provisionCustomer, sendEmailVerification,
  generateBookingConfirmation,
} from "../api/bookingsApi"

// ── useBookings (list) ───────────────────────────────────────────────────────
export function useBookings() {
  const [data, setData] = useState(null) // { bookings, meta }
  const [loading, setLoading] = useState(false)
  const abortRef = useRef(null)

  const fetch = useCallback(async (filters = {}) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()
    setLoading(true)
    try {
      setData(await fetchBookings(filters))
    } catch (err) {
      if (err.name !== "AbortError") { /* swallow; list shows empty */ }
    } finally {
      setLoading(false)
    }
  }, [])

  return { data, loading, fetch, refresh: fetch }
}

// ── useBooking (detail) ──────────────────────────────────────────────────────
export function useBooking(id) {
  const [booking, setBooking] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    fetchBooking(id).then(setBooking).catch(() => {}).finally(() => setLoading(false))
  }, [id])

  useEffect(() => { load() }, [load])

  return { booking, setBooking, loading, reload: load }
}

// ── Mutations ────────────────────────────────────────────────────────────────
function useMutation(fn) {
  const [isPending, setPending] = useState(false)
  const mutate = useCallback(async (...args) => {
    setPending(true)
    try {
      return await fn(...args)
    } catch (err) {
      throw err.response?.data?.errors || [err.message || "Something went wrong"]
    } finally {
      setPending(false)
    }
  }, [fn])
  return { mutate, isPending }
}

export const useCreateBooking     = () => useMutation(createBooking)
export const useUpdateCustomer    = () => useMutation(updateBookingCustomer)
export const useCancelBooking     = () => useMutation(cancelBooking)
export const useCompleteBooking   = () => useMutation(completeBooking)
export const useRecordPayment     = () => useMutation(recordPayment)
export const useProvisionCustomer = () => useMutation(provisionCustomer)
export const useSendEmailVerification = () => useMutation(sendEmailVerification)
export const useGenerateBookingConfirmation = () => useMutation(generateBookingConfirmation)
